/**
 * The lobby: who is here, what games are on offer, and what is being played.
 */

import { useState } from 'react';

import {
  FORMATION_NAMES,
  formationInfo,
  type FormationName,
  type Variant,
} from '@gok/engine';
import { type LobbyState, reduceLobby } from '@gok/shared';

import { useChannel, useConnection } from '../net/context.tsx';
import { UserName, useUser } from '../net/useUser.tsx';
import { LEARN_HREF, matchHref, PLAY_HREF } from './router.ts';
import { AccountDialog } from './Account.tsx';
import { Button, Empty, Field, Panel, Select } from '../ui/primitives.tsx';
import { ConnectionBadge } from '../ui/Chrome.tsx';

/** Time controls, named the way players talk about them. */
const TIME_CONTROLS = [
  { label: 'Blitz · 3+2', initial: 180_000, increment: 2_000 },
  { label: 'Rapid · 10+5', initial: 600_000, increment: 5_000 },
  { label: 'Classical · 30+15', initial: 1_800_000, increment: 15_000 },
] as const;

export function Lobby() {
  const { state, stale } = useChannel<LobbyState, never>('lobby', reduceLobby);
  const { status } = useConnection();

  return (
    <div className='mx-auto grid w-full max-w-5xl gap-4 p-4 lg:grid-cols-[1fr_20rem]'>
      <div className='flex flex-col gap-4'>
        <div className='grid gap-3 sm:grid-cols-2'>
          <FirstTime />
          <PlayTheBot />
        </div>
        <NewGame />
        <Panel
          title='Open challenges'
          actions={<ConnectionBadge status={status} stale={stale} />}
        >
          {!state || state.challenges.length === 0
            ? (
              <Empty>
                Nobody is waiting for a game. Post a challenge and it appears here — or
                {' '}
                <a href={PLAY_HREF} className='text-accent hover:underline'>
                  play the bot
                </a>{' '}
                while you wait.
              </Empty>
            )
            : (
              <ul className='divide-y divide-rule'>
                {state.challenges.map((challenge) => (
                  <ChallengeRow key={challenge.id} challenge={challenge} />
                ))}
              </ul>
            )}
        </Panel>

        <Panel title='In progress'>
          <MatchRows
            matches={state?.live ?? []}
            empty='No games are being played right now.'
          />
        </Panel>

        <Panel title='Recently finished'>
          <MatchRows
            matches={state?.recent ?? []}
            empty='Nothing has finished yet.'
          />
        </Panel>
      </div>

      <Panel
        title={`Here now${state ? ` · ${state.users.length}` : ''}`}
        className='h-fit'
      >
        {!state || state.users.length === 0
          ? <Empty>Just you.</Empty>
          : (
            <ul className='divide-y divide-rule'>
              {state.users.map((userId) => (
                <li key={userId} className='px-3 py-1.5 text-sm'>
                  <UserName userId={userId} />
                </li>
              ))}
            </ul>
          )}
      </Panel>
    </div>
  );
}

/**
 * The way in for someone who has never seen the game.
 *
 * It sits above the challenge form on purpose: the rules take thirty seconds to learn and
 * one of them — the bow — is not like anything in another board game, so sending a new
 * player straight into a rated match against a stranger is not doing them a favour.
 */
function FirstTime() {
  return (
    <a
      href={LEARN_HREF}
      className='flex flex-col gap-1 rounded-lg border border-accent bg-accent-soft
        px-4 py-3 text-sm transition-colors hover:brightness-105'
    >
      <strong className='font-semibold text-accent'>New here?</strong>
      <span className='text-ink-2'>
        Two pieces, one board, and a bow that shoots one of your own pawns across it. Five
        minutes, against nobody.
      </span>
      <span className='mt-auto pt-1 font-mono text-xs text-accent'>
        How to play &rarr;
      </span>
    </a>
  );
}

/**
 * The other way in, and the answer to an empty lobby.
 *
 * An empty lobby is the real risk at relaunch: somebody arrives, finds nobody waiting,
 * and leaves. A bot that runs in their own browser means there is always a game, and it
 * costs nothing to offer — which is why it is on the front page rather than tucked behind
 * a menu.
 */
function PlayTheBot() {
  return (
    <a
      href={PLAY_HREF}
      className='flex flex-col gap-1 rounded-lg border border-rule bg-surface px-4 py-3
        text-sm transition-colors hover:border-rule-strong hover:bg-raised'
    >
      <strong className='font-semibold text-ink'>Play the bot</strong>
      <span className='text-ink-2'>
        Four difficulties, no waiting and no account. It runs in this tab, so it works
        even when nobody else is here.
      </span>
      <span className='mt-auto pt-1 font-mono text-xs text-muted'>
        Start a game &rarr;
      </span>
    </a>
  );
}

function NewGame() {
  const { send, status } = useConnection();
  const [formation, setFormation] = useState<FormationName>('monarchy');
  const [radius, setRadius] = useState(4);
  const [control, setControl] = useState(1);
  const [rated, setRated] = useState(true);

  const minRadius = formationInfo(formation).minRadius;
  const radii = [2, 3, 4, 5, 6].filter((r) => r >= minRadius);
  const effectiveRadius = Math.max(radius, minRadius);

  const post = () => {
    const variant: Variant = {
      radius: effectiveRadius,
      formation,
      spawnsAvailable: 12,
      timeInitialMs: TIME_CONTROLS[control].initial,
      timeIncrementMs: TIME_CONTROLS[control].increment,
      stakes: rated ? 1 : 0,
    };
    send({ t: 'challenge', variant });
  };

  return (
    <Panel title='New game'>
      <div className='flex flex-wrap items-end gap-3 p-3'>
        <Field label='Formation'>
          <Select
            value={formation}
            onChange={(e) => setFormation(e.target.value as FormationName)}
          >
            {FORMATION_NAMES.map((name) => (
              <option key={name} value={name}>
                {name} · {formationInfo(name).kingsPerPlayer}{' '}
                king{formationInfo(name).kingsPerPlayer > 1 ? 's' : ''}
              </option>
            ))}
          </Select>
        </Field>

        <Field label='Board'>
          <Select
            value={effectiveRadius}
            onChange={(e) => setRadius(Number(e.target.value))}
          >
            {radii.map((r) => <option key={r} value={r}>radius {r}</option>)}
          </Select>
        </Field>

        <Field label='Clock'>
          <Select
            value={control}
            onChange={(e) => setControl(Number(e.target.value))}
          >
            {TIME_CONTROLS.map((c, i) => (
              <option key={c.label} value={i}>{c.label}</option>
            ))}
          </Select>
        </Field>

        <label className='flex items-center gap-2 pb-1.5 text-sm text-ink-2'>
          <input
            type='checkbox'
            checked={rated}
            onChange={(e) => setRated(e.target.checked)}
            className='accent-accent'
          />
          Rated
        </label>

        <Button
          tone='primary'
          onClick={post}
          disabled={status !== 'ready'}
          className='ml-auto'
        >
          Post challenge
        </Button>
      </div>
    </Panel>
  );
}

function ChallengeRow({
  challenge,
}: {
  challenge: LobbyState['challenges'][number];
}) {
  const { send, userId } = useConnection();
  const mine = challenge.challengerId === userId;
  const { variant } = challenge;

  return (
    <li className='flex items-center gap-3 px-3 py-2'>
      <UserName userId={challenge.challengerId} className='flex-1' />
      <span className='font-mono text-[0.72rem] text-muted'>
        {variant.formation} · r{variant.radius} ·{' '}
        {Math.round(variant.timeInitialMs / 60_000)}+
        {Math.round(variant.timeIncrementMs / 1000)}
        {variant.stakes === 0 && ' · casual'}
      </span>
      {mine
        ? (
          <Button onClick={() => send({ t: 'unchallenge', id: challenge.id })}>
            Withdraw
          </Button>
        )
        : (
          <Button
            tone='primary'
            onClick={() => send({ t: 'accept', id: challenge.id })}
          >
            Accept
          </Button>
        )}
    </li>
  );
}

function MatchRows({
  matches,
  empty,
}: {
  matches: readonly { id: string; players: readonly [string, string] }[];
  empty: string;
}) {
  if (matches.length === 0) return <Empty>{empty}</Empty>;
  return (
    <ul className='divide-y divide-rule'>
      {matches.map((match) => (
        <li key={match.id}>
          <a
            href={matchHref(match.id)}
            className='flex items-center gap-2 px-3 py-2 text-sm hover:bg-raised'
          >
            <UserName userId={match.players[0]} seat={0} className='flex-1' />
            <span className='font-mono text-[0.7rem] text-muted'>vs</span>
            <UserName
              userId={match.players[1]}
              seat={1}
              className='flex-1 justify-end'
            />
          </a>
        </li>
      ))}
    </ul>
  );
}

/** The header's "who am I" control. Opens the account dialog. */
export function SelfBadge() {
  const { userId } = useConnection();
  const me = useUser(userId);
  const [open, setOpen] = useState(false);

  if (!me) return <span className='text-xs text-muted'>connecting…</span>;

  return (
    <>
      <button
        type='button'
        onClick={() => setOpen(true)}
        className='flex items-center gap-1.5 rounded-md px-2 py-1 text-sm hover:bg-raised'
        title='Your name, rating and account'
      >
        <span className='font-medium'>{me.username}</span>
        <span className='tabular font-mono text-[0.7rem] text-muted'>
          {Math.round(me.rating.mean)}
        </span>
        {!me.registered && (
          <span className='rounded bg-raised px-1 font-mono text-[0.6rem] text-muted'>
            guest
          </span>
        )}
      </button>
      <AccountDialog open={open} onClose={() => setOpen(false)} />
    </>
  );
}
